'use client';

import ReservationsRadarChart from '@/components/chart/ReservationsRadarChart';
import StackedBarChart from '@/components/chart/StackedBarChart';
import { getMondayBasedWeekdayIndex, getWeekdayLabels } from '@/components/chart/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/design-system/select';
import { useDynamicLocale } from '@/hoslog-intl';
import { ChevronLeft, ChevronRight } from '@/icons';
import { exportToCSV } from '@/lib/exportToCSV';
import { ChevronDown, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from 'ui/button';
import { usePaymentAnalytics, usePaymentOverviewLocations, usePaymentReservationChart } from '../hooks/useOverview';

const PAYMENT_CHART_COLORS = [
  'var(--chart-payment-1)',
  'var(--chart-payment-2)',
  'var(--chart-payment-3)',
  'var(--chart-payment-4)',
  'var(--chart-payment-5)',
  'var(--chart-payment-6)',
];

const getWeekRange = (weekOffset: number) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysSinceMonday = start.getDay() === 0 ? 6 : start.getDay() - 1;
  start.setDate(start.getDate() - daysSinceMonday - weekOffset * 7);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const formatDate = (date: Date) =>
  `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;

type ReservationTimeframe = 'this-week' | 'today' | 'last-15-days' | 'this-month' | 'last-month';

const getReservationDateRange = (timeframe: ReservationTimeframe) => {
  if (timeframe === 'this-week') return getWeekRange(0);

  const today = new Date();
  if (timeframe === 'today') {
    return {
      start: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
    };
  }
  if (timeframe === 'last-15-days') {
    return {
      start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14),
      end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
    };
  }
  if (timeframe === 'last-month') {
    return {
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999),
    };
  }

  return {
    start: new Date(today.getFullYear(), today.getMonth(), 1),
    end: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999),
  };
};

export const Analytics = () => {
  const { locale, t } = useDynamicLocale();
  const [weekOffset, setWeekOffset] = useState(0);
  const [reservationTimeframe, setReservationTimeframe] = useState<ReservationTimeframe>('this-week');
  const { start, end } = useMemo(() => getWeekRange(weekOffset), [weekOffset]);
  const analyticsDateParams = useMemo(() => ({ fromDate: start.getTime(), toDate: end.getTime() }), [start, end]);
  const reservationDateRange = useMemo(() => getReservationDateRange(reservationTimeframe), [reservationTimeframe]);
  const reservationDateParams = useMemo(
    () => ({ fromDate: reservationDateRange.start.getTime(), toDate: reservationDateRange.end.getTime() }),
    [reservationDateRange],
  );
  const { paymentAnalytics } = usePaymentAnalytics(analyticsDateParams);
  const { reservationChart } = usePaymentReservationChart(reservationDateParams);
  const { locations } = usePaymentOverviewLocations();
  const currentDateRange = `${formatDate(start)} ~ ${formatDate(end)}`;
  const weekdayLabels = useMemo(() => getWeekdayLabels(t), [t]);

  const planSummary = paymentAnalytics?.planSummary ?? [];
  const chartSeries = planSummary.map((plan, index) => ({
    key: `plan-${index}`,
    name: locale === 'en' ? plan.planNameEng || plan.planName : plan.planName,
    color: PAYMENT_CHART_COLORS[index % PAYMENT_CHART_COLORS.length],
    percentage: plan.percentage,
  }));
  const planKeys = new Map(planSummary.map((plan, index) => [plan.planName, `plan-${index}`]));
  const chartData = weekdayLabels.map((day) => {
    const row: Record<string, string | number> = { day };
    chartSeries.forEach((series) => {
      row[series.key] = 0;
    });
    return row;
  });
  (paymentAnalytics?.analytics ?? []).forEach((item) => {
    const row = chartData[getMondayBasedWeekdayIndex(new Date(item.date))];
    if (!row) return;

    item.plans.forEach((plan) => {
      const key = planKeys.get(plan.planName);
      if (key) row[key] = Number(row[key] ?? 0) + plan.count;
    });
  });
  const hasChartData = chartData.some((row) => chartSeries.some((series) => Number(row[series.key] ?? 0) > 0));
  const totalReservations = reservationChart?.totalReservations ?? 0;
  const reservationChartData = locations.map((location) => {
    const reservationLocation = reservationChart?.locations?.find(
      ({ locationId }) => locationId === location.locationId,
    );

    return {
      region:
        locale === 'en'
          ? location.locationEngTitle || location.locationTitle || location.locationName
          : location.locationTitle || location.locationName,
      score: totalReservations > 0 ? ((reservationLocation?.totalReservation ?? 0) / totalReservations) * 100 : 0,
      maxScore: 100,
    };
  });

  const onExportPlansCSV = () => {
    if (!hasChartData) return;

    const exportRows = chartData.map((row) =>
      chartSeries.reduce<Record<string, string | number>>(
        (result, series) => ({ ...result, [series.name]: row[series.key] ?? 0 }),
        { [t('common.page.tableColumn.date')]: row.day ?? '' },
      ),
    );
    exportToCSV(exportRows, { filename: `plans-usage-analytics-${currentDateRange.replace(/\s|~/g, '-')}` });
  };

  return (
    <div className="flex flex-col xl:flex-row gap-6  items-stretch">
      {/* Left Card: Plans Usage Analytics (~60% width) */}
      <div className="flex-1 min-w-0 rounded-sm  bg-card p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-bold text-foreground-heading">
            {t('paymentManagement.overview.analytics.plansUsageTitle')}
          </h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Button
                aria-label={t('common.page.button.previous')}
                variant="icon"
                size="icon"
                className="size-5  hover:bg-surface-hover text-foreground-muted"
                onClick={() => setWeekOffset((current) => current + 1)}
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <span className="text-xs font-semibold text-foreground-heading">{currentDateRange}</span>
              <Button
                aria-label={t('common.page.button.next')}
                variant="icon"
                size="icon"
                className="size-5  hover:bg-surface-hover text-foreground-muted"
                onClick={() => setWeekOffset((current) => Math.max(0, current - 1))}
                disabled={weekOffset === 0}
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7.5 px-3 bg-surface-hover hover:bg-surface-active text-primary-950 border-border text-xs font-medium flex items-center gap-1.5 shadow-none"
              onClick={onExportPlansCSV}
              disabled={!hasChartData}
            >
              <FileText className="size-3.5" />
              {t('common.page.table.exportCSVText')}
            </Button>
          </div>
        </div>

        {hasChartData ? (
          <div className="h-[360px] w-full">
            <StackedBarChart
              data={chartData}
              series={chartSeries}
              useWeekdayLabels={false}
              height="100%"
              barSize={6}
              showGrid={true}
              valueFormatter={(val, name) => `${name ?? ''}: ${val}`}
            />
          </div>
        ) : (
          <div className="flex h-[360px] items-center justify-center rounded-md border border-empty-border bg-empty-background px-6 text-center">
            <div>
              <p className="text-lg font-semibold text-empty-foreground">
                {t('paymentManagement.overview.analytics.emptyTitle')}
              </p>
              <p className="mt-2 text-sm text-empty-description">
                {t('paymentManagement.overview.analytics.emptyDescription')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Right Card: Reservations Chart (~40% width) */}
      <div className="w-full xl:w-[400px] 2xl:w-[440px] shrink-0 rounded-sm bg-card p-5 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-foreground-heading">
            {t('paymentManagement.overview.analytics.reservationTitle')}
          </h3>
          <Select
            value={reservationTimeframe}
            onValueChange={(value) => setReservationTimeframe(value as ReservationTimeframe)}
          >
            <SelectTrigger
              icon={<ChevronDown className="size-4" />}
              className="h-7.5 w-[115px] text-xs font-medium border-border bg-card cursor-pointer"
            >
              <SelectValue placeholder={t('paymentManagement.overview.analytics.timeframe.thisWeek')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-week">{t('paymentManagement.overview.analytics.timeframe.thisWeek')}</SelectItem>
              <SelectItem value="today">{t('paymentManagement.overview.analytics.timeframe.today')}</SelectItem>
              <SelectItem value="last-15-days">
                {t('paymentManagement.overview.analytics.timeframe.last15Days')}
              </SelectItem>
              <SelectItem value="this-month">
                {t('paymentManagement.overview.analytics.timeframe.thisMonth')}
              </SelectItem>
              <SelectItem value="last-month">
                {t('paymentManagement.overview.analytics.timeframe.lastMonth')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="h-[360px] w-full flex items-center justify-center">
          <ReservationsRadarChart data={reservationChartData} height={320} />
        </div>
      </div>
    </div>
  );
};
